import { useCallback, useState, useEffect, useMemo, useRef } from "react";
import { Card } from "react-bootstrap";
import Button from "app/storybookComponents/Button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import IntegrationSetupModal from "app/components/Modals/IntegrationSetupModal";
import IntegrationDisconnectModal from "app/components/Modals/IntegrationDisconnectModal";
import { INTEGRATIONS } from "../constants";
import { toast } from "react-toastify";
import {
  fetchIntegrationStatus,
  sendTestMessage,
} from "app/containers/AdminConsole/slice";
import { useAppDispatch, useAppSelector } from "utils/redux/hooks";
import { IntegrationDefinition } from "../types";
import { selectConfigCatFlag } from "app/containers/Global/slice";

interface IntegrationStatus {
  [key: string]: boolean;
}

export default function PlatformIntegrations() {
  const [integrationStatus, setIntegrationStatus] = useState<IntegrationStatus>(
    {}
  );
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showDisconnectModal, setShowDisconnectModal] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState<string | null>(null);
  const [selectedPlatformForTest, setSelectedPlatformForTest] = useState<
    string | null
  >(null);
  const [disconnectPlatform, setDisconnectPlatform] = useState<string | null>(
    null
  );
  const hasFetchedRef = useRef(false);

  const dispatch = useAppDispatch();

  const isTeamsIntegrationEnabled = useAppSelector(
    selectConfigCatFlag("teamsIntegration")
  );

  const integrations = useMemo(() => {
    return INTEGRATIONS.filter((integration) => {
      if (integration.id === "microsoft-teams") {
        return isTeamsIntegrationEnabled;
      }
      return true;
    });
  }, [isTeamsIntegrationEnabled]);

  const fetchStatuses = useCallback(async () => {
    if (integrations.length === 0) {
      setLoading(false);
      return;
    }

    setLoading(true);
    const statusMap: IntegrationStatus = {};

    // Fetch all integration statuses independently using Promise.allSettled
    const results = await Promise.allSettled(
      integrations.map(async (item) => {
        const action = await dispatch(fetchIntegrationStatus(item.id));
        const result = action.payload as any;
        return { id: item.id, hasIntegration: Boolean(result?.hasIntegration) };
      })
    );

    // Process results - each integration is handled independently
    results.forEach((result) => {
      if (result.status === "fulfilled") {
        statusMap[result.value.id] = result.value.hasIntegration;
      } else {
        // Log individual failures but don't block other integrations
        console.error("Failed to load integration status:", result.reason);
      }
    });

    setIntegrationStatus(statusMap);
    setLoading(false);
  }, [dispatch, integrations]);

  useEffect(() => {
    // Wait until integrations are known
    if (integrations.length === 0) return;

    // Prevent re-fetching
    if (hasFetchedRef.current) return;

    hasFetchedRef.current = true;
    fetchStatuses();
  }, [integrations, fetchStatuses]);

  const handleSendTestMessage = async (provider: string) => {
    setSelectedPlatformForTest(provider);
    setSendingTest(true);

    try {
      const result = await dispatch(sendTestMessage(provider)).unwrap();
      // result = { success: true }

      if (result.success) {
        toast.success(`A test message has been sent to your ${provider}!`);
      } else {
        toast.error("Failed to send test message.");
      }
    } catch (err: any) {
      toast.error(err || "Could not send test message. Please try again.");
    } finally {
      setSendingTest(false);
    }
  };

  const getIntegrationButton = ({ id, icon, name }: IntegrationDefinition) => {
    if (loading) {
      return <span className="text-muted small">Checking status...</span>;
    }

    if (integrationStatus[id]) {
      return (
        <div className="d-flex gap-2">
          {/* Disconnect button */}
          <Button
            variant="danger"
            onClick={() => {
              setDisconnectPlatform(id);
              setShowDisconnectModal(true);
            }}
          >
            <FontAwesomeIcon icon={icon} className="me-2" />
            Disconnect
          </Button>

          <Button
            variant="secondary-blue"
            disabled={sendingTest && selectedPlatformForTest === id}
            onClick={() => handleSendTestMessage(id)}
          >
            {sendingTest && selectedPlatformForTest === id
              ? "Sending..."
              : "Send Test Message"}
          </Button>
        </div>
      );
    }

    return (
      <Button
        variant="secondary-blue"
        onClick={() => {
          setSelectedPlatform(id as any);
          setShowModal(true);
        }}
      >
        <FontAwesomeIcon icon={icon} className="me-2" />
        Add to {name.split(" ")[1] ?? name}
      </Button>
    );
  };

  if (integrations.length === 0) {
    return null;
  }

  return (
    <Card className="admin-console-settings-card">
      <div>
        <h2>Platform Integrations</h2>
        <p>
          Connect Coach Bo to supported communication and messaging platforms.
        </p>
      </div>

      {integrations.map((item) => (
        <div className="setting-row" key={item.id}>
          <div className="setting-row-description">
            <h3>{item.name}</h3>
            <p>{item.description}</p>
          </div>
          <div className="setting-row-action d-flex align-items-center">
            {getIntegrationButton(item)}
          </div>
        </div>
      ))}
      {selectedPlatform && (
        <IntegrationSetupModal
          show={showModal}
          hideModal={() => {
            setShowModal(false);
          }}
          platform={selectedPlatform}
          onRefreshIntegrationStatus={() => {
            fetchStatuses(); //refresh after closing modal
          }}
        />
      )}
      {disconnectPlatform && (
        <IntegrationDisconnectModal
          show={showDisconnectModal}
          hideModal={() => setShowDisconnectModal(false)}
          platform={disconnectPlatform}
          onDisconnected={fetchStatuses} // refresh list after disconnect
        />
      )}
    </Card>
  );
}
